<template>
  <div class="tin-root">
    <div class="ambient"></div>
    <div class="ambient-floor"></div>
    <div class="grain"></div>
    <div class="frame"></div>
    <div class="frame-inner"></div>

    <div class="page">

      <!-- ── SEARCH ── -->
      <div class="search-wrap">
        <div class="search-box">
          <span class="search-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </span>
          <input
            v-model="searchText"
            class="search-input"
            type="text"
            placeholder="Search by name or title…"
          />
          <div class="search-bar-line"></div>
          <button v-if="searchText" class="search-clear" @click="searchText = ''">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <!-- ── SECTION HEADER ── -->
      <div class="section-hd">
        <div class="section-rule l"></div>
        <span class="section-eyebrow">Your Circle</span>
        <div class="section-rule"></div>
      </div>
      <h2 class="section-title">My <em>Connections</em></h2>

      <!-- ── LOADING ── -->
      <div v-if="isLoading" class="loading-row">
        <div class="loading-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="loading-text">Loading your circle…</p>
      </div>

      <!-- ── EMPTY ── -->
      <div v-else-if="filteredConnections.length === 0" class="empty-state">
        <div class="empty-seal">
          <div class="seal-ring"></div>
          <div class="seal-ring-dashed"></div>
          <div class="seal-diamond"></div>
        </div>
        <p class="empty-title">
          {{ searchText ? 'No results found' : 'No connections yet' }}
        </p>
        <p class="empty-sub">
          {{ searchText ? 'Try a different name or title.' : 'Your connections will appear here.' }}
        </p>
      </div>

      <!-- ── CONNECTIONS GRID ── -->
      <div v-else class="cards-grid">
        <div
          v-for="conn in filteredConnections"
          :key="conn.uid"
          class="conn-card"
          @click="goToMemberDetails(conn.uid)"
        >
          <!-- Corner brackets -->
          <div class="corner tl"></div>
          <div class="corner br"></div>

          <div class="card-inner">
            <div class="card-avatar">
              <img v-if="conn.image" :src="conn.image" :alt="conn.fullName" />
              <span v-else>{{ (conn.fullName || '?').charAt(0) }}</span>
            </div>
            <div class="card-info">
              <p class="card-name">{{ conn.fullName }}</p>
              <p class="card-title">{{ conn.title || conn.baseCity || 'Member' }}</p>
            </div>
            <svg class="card-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </div>

          <div class="card-footer">
            <button class="view-btn" @click.stop="goToMemberDetails(conn.uid)">
              <div class="btn-tick"></div>
              <span>View Profile</span>
              <div class="btn-tick"></div>
            </button>
            <button
              class="disconnect-btn"
              @click.stop="confirmDisconnect(conn)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
        </div>
      </div>

    </div>

    <!-- ── DISCONNECT DIALOG ── -->
    <div v-if="showDisconnectDialog" class="overlay" @click.self="showDisconnectDialog = false">
      <div class="panel">
        <div class="panel-corner tl"></div>
        <div class="panel-corner br"></div>

        <div class="panel-hd">
          <div class="section-hd">
            <div class="section-rule l"></div>
            <span class="section-eyebrow">Confirm</span>
            <div class="section-rule"></div>
          </div>
          <h3 class="panel-title">Disconnect <em>from Circle</em></h3>
        </div>

        <p class="panel-body">
          Are you sure you want to disconnect from
          <span class="panel-name">{{ selectedConn?.fullName }}</span>?
          This cannot be undone.
        </p>

        <div class="panel-actions">
          <button class="panel-confirm" @click="disconnect">
            <div class="btn-tick"></div>
            <span>Yes, Disconnect</span>
            <div class="btn-tick"></div>
          </button>
          <button class="panel-cancel" @click="showDisconnectDialog = false">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ── BOTTOM NAV ── -->
    <nav class="bottom-nav">
      <div class="bottom-nav-glow"></div>
      <button class="nav-item" @click="$router.push('/home')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        <span class="nav-label">Home</span>
      </button>
      <button class="nav-item" @click="$router.push('/search')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <span class="nav-label">Search</span>
      </button>
      <button class="nav-item" @click="$router.push('/explore')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
        <span class="nav-label">Explore</span>
      </button>
      <button class="nav-item" @click="$router.push('/messages')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
        <span class="nav-label">Messages</span>
      </button>
      <button class="nav-item" @click="$router.push('/groups')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
        <span class="nav-label">Groups</span>
      </button>
      <button class="nav-item" @click="$router.push('/member-settings')">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        <span class="nav-label">Profile</span>
      </button>
    </nav>
  </div>
</template>

<script>
import { getAuth } from "firebase/auth";
import { db } from "@/firebase";
import { doc, getDoc, getDocs, collection, deleteDoc } from "firebase/firestore";

export default {
  name: "ConnectionsView",

  data() {
    return {
      user: {},
      connections: [],
      searchText: "",
      showDisconnectDialog: false,
      selectedConn: null,
      isLoading: true,
    };
  },

  computed: {
    filteredConnections() {
      const search = this.searchText.toLowerCase();
      return this.connections.filter(
        (conn) =>
          conn.fullName?.toLowerCase().includes(search) ||
          conn.title?.toLowerCase().includes(search)
      );
    },
  },

  async mounted() {
    await this.fetchUser();
    await this.fetchConnections();
  },

  methods: {
    async fetchUser() {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const docSnap = await getDoc(doc(db, "user", currentUser.uid));
        if (docSnap.exists()) {
          this.user = { uid: currentUser.uid, ...docSnap.data() };
        }
      } catch (error) {
        console.error("Error fetching user:", error);
      }
    },

    async fetchConnections() {
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        const connSnap = await getDocs(
          collection(db, `user/${currentUser.uid}/connections`)
        );
        const rawConnections = connSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));

        // Same fix as HomeView.vue — resolve against the live user doc instead
        // of trusting denormalized fields that can be missing or stale, and
        // drop the connection entirely if the account is gone. This view
        // was already effectively hiding those ghost entries, but only by
        // accident: filteredConnections' search filter happens to drop any
        // entry with no fullName because `undefined?.toLowerCase()` is
        // falsy. That's fragile — it silently breaks if the filter logic
        // ever changes — so this makes the real intent explicit instead of
        // relying on that side effect.
        const resolvedConnections = await Promise.all(
          rawConnections.map(async (conn) => {
            try {
              const userSnap = await getDoc(doc(db, "user", conn.uid));
              if (userSnap.exists()) {
                const userData = userSnap.data();
                return {
                  ...conn,
                  fullName: userData.fullName || conn.fullName || "",
                  image: userData.image || conn.image || "",
                  // Shown instead of a flat "Member" fallback when there's
                  // no title — matches the same field iOS's Personal
                  // Information page and MemberDetailView.vue both use.
                  baseCity: userData.baseCity || conn.baseCity || "",
                };
              }
              return null;
            } catch (e) {
              console.error("Failed to resolve connection profile:", e);
              return conn; // a fetch error isn't the same as "this account is gone" — keep it rather than guess
            }
          })
        );
        this.connections = resolvedConnections.filter(Boolean);
      } catch (err) {
        console.error("Error fetching connections:", err);
      } finally {
        this.isLoading = false;
      }
    },

    goToMemberDetails(uid) {
      this.$router.push({ name: "MemberDetail", params: { id: uid } });
    },

    confirmDisconnect(conn) {
      this.selectedConn = conn;
      this.showDisconnectDialog = true;
    },

    async disconnect() {
      if (!this.selectedConn) return;
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        await deleteDoc(
          doc(db, `user/${currentUser.uid}/connections`, this.selectedConn.uid)
        );
        this.connections = this.connections.filter(
          (c) => c.uid !== this.selectedConn.uid
        );
      } catch (err) {
        console.error("Error disconnecting:", err);
      } finally {
        this.showDisconnectDialog = false;
        this.selectedConn = null;
      }
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600&display=swap');

.tin-root { position: relative; min-height: 100vh; width: 100%; background: #070708; font-family: 'Montserrat', sans-serif; }

.ambient { position: fixed; top: -300px; left: 50%; transform: translateX(-50%); width: 1000px; height: 800px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 10%, rgba(210,175,100,0.09) 0%, rgba(210,175,100,0.03) 30%, transparent 65%); }
.ambient-floor { position: fixed; bottom: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 400px; pointer-events: none; z-index: 0; background: radial-gradient(ellipse at 50% 100%, rgba(210,175,100,0.03) 0%, transparent 60%); }
.grain { position: fixed; inset: 0; z-index: 1; pointer-events: none; opacity: 0.5; background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.12'/%3E%3C/svg%3E"); background-size: 180px 180px; }
.frame { position: fixed; inset: 20px; border: 1px solid rgba(210,175,100,0.07); z-index: 2; pointer-events: none; }
.frame::before, .frame::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame::before { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.frame::after  { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner { position: fixed; inset: 20px; z-index: 2; pointer-events: none; }
.frame-inner::before, .frame-inner::after { content: ''; position: absolute; width: 18px; height: 18px; }
.frame-inner::before { top: -1px; right: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.frame-inner::after  { bottom: -1px; left: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }

.page { position: relative; z-index: 10; max-width: 1100px; margin: 0 auto; padding: 52px 48px 140px; animation: fadeUp 0.8s cubic-bezier(0.16,1,0.3,1) both; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

/* ── SEARCH ── */
.search-wrap { display: flex; justify-content: center; margin-bottom: 56px; }
.search-box { position: relative; width: 100%; max-width: 560px; }
.search-icon { position: absolute; left: 0; top: 50%; transform: translateY(-50%); color: rgba(210,175,100,0.4); pointer-events: none; display: flex; }
.search-icon svg { width: 18px; height: 18px; }
.search-input { width: 100%; background: transparent; border: none; border-bottom: 1px solid rgba(237,232,223,0.15); padding: 14px 36px 14px 32px; color: #edeae2; font-family: 'Montserrat', sans-serif; font-size: 15px; font-weight: 300; outline: none; caret-color: rgba(210,175,100,0.8); transition: border-color 0.3s; letter-spacing: 0.3px; }
.search-input::placeholder { color: rgba(237,232,223,0.2); font-style: italic; }
.search-input:focus { border-bottom-color: rgba(210,175,100,0.4); }
.search-bar-line { position: absolute; bottom: 0; left: 0; height: 1px; width: 0; background: linear-gradient(90deg, rgba(210,175,100,0.8), rgba(210,175,100,0.2)); transition: width 0.5s cubic-bezier(0.25,0.46,0.45,0.94); pointer-events: none; }
.search-input:focus ~ .search-bar-line { width: 100%; }
.search-clear { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: rgba(237,232,223,0.25); display: flex; align-items: center; transition: color 0.2s; padding: 4px; }
.search-clear:hover { color: rgba(210,175,100,0.6); }
.search-clear svg { width: 14px; height: 14px; }

/* ── SECTION HEADER ── */
.section-hd { display: flex; align-items: center; gap: 20px; margin-bottom: 20px; }
.section-rule { flex: 1; height: 1px; background: linear-gradient(90deg, rgba(210,175,100,0.18), transparent); }
.section-rule.l { background: linear-gradient(90deg, transparent, rgba(210,175,100,0.18)); }
.section-eyebrow { font-size: 9px; font-weight: 600; letter-spacing: 4px; color: rgba(210,175,100,0.45); text-transform: uppercase; white-space: nowrap; }
.section-title { font-family: 'Cormorant', serif; font-size: 40px; font-weight: 300; color: #edeae2; letter-spacing: -0.5px; margin-bottom: 32px; line-height: 1; }
.section-title em { font-style: italic; color: rgba(210,175,100,0.8); font-weight: 300; }

/* ── LOADING / EMPTY ── */
.loading-row { display: flex; flex-direction: column; align-items: center; gap: 20px; padding: 80px 0; }
.loading-seal, .empty-seal { position: relative; width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.seal-ring { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); animation: spinSlow 6s linear infinite; }
.seal-ring-dashed { position: absolute; inset: 5px; border-radius: 50%; border: 1px dashed rgba(210,175,100,0.15); animation: spinSlow 4s linear infinite reverse; }
@keyframes spinSlow { to { transform: rotate(360deg); } }
.seal-diamond { width: 12px; height: 12px; border: 1px solid rgba(210,175,100,0.6); transform: rotate(45deg); position: relative; z-index: 1; }
.seal-diamond::after { content: ''; position: absolute; inset: 2px; background: rgba(210,175,100,0.3); }
.loading-text { font-size: 10px; font-weight: 500; letter-spacing: 3px; color: rgba(237,232,223,0.2); text-transform: uppercase; }
.empty-state { display: flex; flex-direction: column; align-items: center; padding: 80px 0; border: 1px solid rgba(210,175,100,0.08); }
.empty-title { font-family: 'Cormorant', serif; font-size: 26px; font-weight: 300; color: rgba(237,232,223,0.3); margin-bottom: 10px; }
.empty-sub { font-size: 11px; font-weight: 400; letter-spacing: 1.5px; color: rgba(237,232,223,0.15); text-transform: uppercase; }

/* ── CARDS GRID ── */
.cards-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }

.conn-card { border: 1px solid rgba(210,175,100,0.1); background: rgba(210,175,100,0.02); cursor: pointer; transition: border-color 0.3s; position: relative; overflow: hidden; }
.conn-card::before, .conn-card::after { content: ''; position: absolute; width: 0; height: 0; }
.corner { position: absolute; width: 12px; height: 12px; z-index: 1; pointer-events: none; }
.corner.tl { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.35); border-left: 1px solid rgba(210,175,100,0.35); }
.corner.br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.35); border-right: 1px solid rgba(210,175,100,0.35); }
.conn-card:hover { border-color: rgba(210,175,100,0.3); }

.card-inner { display: flex; align-items: center; gap: 16px; padding: 20px 20px 0; }
.card-avatar { width: 52px; height: 52px; border-radius: 50%; border: 1px solid rgba(210,175,100,0.2); overflow: hidden; background: rgba(210,175,100,0.05); display: flex; align-items: center; justify-content: center; font-family: 'Cormorant', serif; font-size: 20px; font-weight: 300; color: rgba(210,175,100,0.4); flex-shrink: 0; transition: border-color 0.2s; }
.conn-card:hover .card-avatar { border-color: rgba(210,175,100,0.45); }
.card-avatar img { width: 100%; height: 100%; object-fit: cover; object-position: top; }
.card-info { flex: 1; min-width: 0; }
.card-name { font-family: 'Cormorant', serif; font-size: 20px; font-weight: 400; color: #edeae2; line-height: 1.1; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.card-title { font-size: 9px; font-weight: 500; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(210,175,100,0.4); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.card-arrow { width: 14px; height: 14px; color: rgba(210,175,100,0.2); flex-shrink: 0; transition: color 0.2s, transform 0.2s; }
.conn-card:hover .card-arrow { color: rgba(210,175,100,0.55); transform: translateX(2px); }

.card-footer { display: flex; align-items: center; border-top: 1px solid rgba(210,175,100,0.08); margin-top: 16px; }
.view-btn { flex: 1; background: transparent; border: none; padding: 13px 0; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3px; text-transform: uppercase; color: rgba(210,175,100,0.5); display: flex; align-items: center; justify-content: center; gap: 9px; position: relative; overflow: hidden; transition: color 0.2s; }
.view-btn::before { content: ''; position: absolute; inset: 0; background: rgba(210,175,100,0.05); transform: scaleX(0); transform-origin: left; transition: transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94); }
.conn-card:hover .view-btn::before { transform: scaleX(1); }
.view-btn:hover { color: rgba(210,175,100,0.9); }
.btn-tick { width: 12px; height: 1px; background: rgba(210,175,100,0.3); flex-shrink: 0; transition: width 0.3s; }
.conn-card:hover .btn-tick { width: 16px; }

.disconnect-btn { width: 44px; height: 44px; border: none; border-left: 1px solid rgba(210,175,100,0.08); background: transparent; cursor: pointer; color: rgba(237,232,223,0.15); display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: color 0.2s, background 0.2s; }
.disconnect-btn:hover { color: rgba(190,65,65,0.7); background: rgba(180,55,55,0.06); }
.disconnect-btn svg { width: 14px; height: 14px; }

/* ── DISCONNECT PANEL ── */
.overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.85); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 24px; }
.panel { background: #0a0a0b; border: 1px solid rgba(210,175,100,0.15); width: 100%; max-width: 440px; padding: 36px; position: relative; }
.panel-corner { position: absolute; width: 14px; height: 14px; }
.panel-corner.tl { top: -1px; left: -1px; border-top: 1px solid rgba(210,175,100,0.5); border-left: 1px solid rgba(210,175,100,0.5); }
.panel-corner.br { bottom: -1px; right: -1px; border-bottom: 1px solid rgba(210,175,100,0.5); border-right: 1px solid rgba(210,175,100,0.5); }
.panel-hd { margin-bottom: 20px; }
.panel-title { font-family: 'Cormorant', serif; font-size: 28px; font-weight: 300; color: #edeae2; line-height: 1.1; }
.panel-title em { font-style: italic; color: rgba(210,175,100,0.8); }
.panel-body { font-size: 13px; font-weight: 300; color: rgba(237,232,223,0.4); line-height: 1.8; margin-bottom: 28px; letter-spacing: 0.2px; }
.panel-name { color: rgba(237,232,223,0.75); font-weight: 400; }
.panel-actions { display: flex; flex-direction: column; gap: 10px; }
.panel-confirm { width: 100%; border: 1px solid rgba(180,60,60,0.35); background: transparent; padding: 14px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 4px; text-transform: uppercase; color: rgba(190,70,70,0.7); display: flex; align-items: center; justify-content: center; gap: 14px; transition: border-color 0.2s, color 0.2s; }
.panel-confirm:hover { border-color: rgba(190,70,70,0.6); color: rgba(200,80,80,0.9); }
.panel-cancel { width: 100%; border: none; background: transparent; padding: 12px; cursor: pointer; font-family: 'Montserrat', sans-serif; font-size: 9px; font-weight: 600; letter-spacing: 3.5px; text-transform: uppercase; color: rgba(237,232,223,0.15); transition: color 0.2s; }
.panel-cancel:hover { color: rgba(237,232,223,0.4); }

/* ── BOTTOM NAV ── */
.bottom-nav { position: fixed; bottom: 0; left: 0; right: 0; z-index: 20; background: #070708; border-top: 1px solid rgba(210,175,100,0.1); display: flex; align-items: center; justify-content: space-around; padding: 14px 0 18px; }
.bottom-nav-glow { position: absolute; top: -1px; left: 10%; right: 10%; height: 1px; background: linear-gradient(90deg, transparent, rgba(210,175,100,0.2), transparent); pointer-events: none; }
.nav-item { display: flex; flex-direction: column; align-items: center; gap: 5px; cursor: pointer; padding: 4px 20px; color: rgba(237,232,223,0.2); transition: color 0.2s; background: none; border: none; font-family: 'Montserrat', sans-serif; min-width: 60px; }
.nav-item.active { color: rgba(210,175,100,0.8); }
.nav-item:hover:not(.active) { color: rgba(237,232,223,0.45); }
.nav-item svg { width: 18px; height: 18px; }
.nav-label { font-size: 9px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }

@media (max-width: 700px) { .cards-grid { grid-template-columns: 1fr; } .page { padding: 40px 24px 120px; } }
@media (max-width: 600px) { .frame, .frame-inner { inset: 12px; } .section-title { font-size: 32px; } .nav-item { padding: 4px 12px; } }
@media (prefers-reduced-motion: reduce) { *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; } }
</style>